import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PageProps } from "../types/auth";
import { trackPageVisit } from "../utils/pageTracker";

import styles from './styles/CurriculumsPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Curriculum {
  curriculum_id: number;
  name: string;
}

const CurriculumsPage: React.FC<PageProps> = ({ user, token, selectedOrgID }) => {
  const [curriculums, setCurriculums] = useState<Curriculum[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [curriculumToDelete, setCurriculumToDelete] = useState<Curriculum | null>(null);
  const navigate = useNavigate();
  const hasTrackedRef = useRef(false);

  // Track page visit only once when component mounts
  useEffect(() => {
    if (!hasTrackedRef.current) {
      trackPageVisit("Curriculums", "/curriculums");
      hasTrackedRef.current = true;
    }
  }, []);

  useEffect(() => {
    const fetchCurriculums = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/curriculums`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch curriculums.");
          return;
        }

        const data: Curriculum[] = await response.json();
        setCurriculums(Array.isArray(data) ? data : []);
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    fetchCurriculums();
  }, [token, selectedOrgID]);

  const handleDeleteClick = (curriculum: Curriculum) => {
    setCurriculumToDelete(curriculum);
  };

  const handleDelete = async (curriculumID: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/curriculums/${curriculumID}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        console.error("Failed to delete curriculum.");
        return;
      }

      setCurriculums(curriculums.filter((c) => c.curriculum_id !== curriculumID));
      setCurriculumToDelete(null);
    } catch (err) {
      console.error("Error deleting curriculum:", err);
    }
  };

  const navigateToCreate = () => {
    navigate("/curriculums/create");
  };

  const navigateToImport = () => {
    navigate("/curriculums/import");
  };

  if (error) {
    return <p className={styles.errorMessage}>{error}</p>;
  }

  const filteredCurriculums = curriculums.filter((curriculum) =>
    curriculum.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Curriculums</h1>

      <div className={styles.searchContainer}>
        <input
          type="text"
          id="search"
          className={styles.searchInput}
          placeholder="Search by curriculum name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className={styles.relatedSection}>
        <h4 className={styles.relatedTitle}>Related</h4>
        <ul className={styles.relatedList}>
          <li className={styles.relatedItem}>
            <a href="/skills" className={styles.relatedLink}>Skills</a>
          </li>
          <li className={styles.relatedItem}>
            <a href="/accomplishments" className={styles.relatedLink}>Accomplishments</a>
          </li>
        </ul>
      </div>

      <div className={styles.actionsContainer}>
        <button className={styles.createButton} onClick={navigateToCreate}>
          Create Curriculum
        </button>
        <button className={styles.importButton} onClick={navigateToImport}>
          Import Curriculum
        </button>
      </div>

      {curriculumToDelete && (
        <>
          <div className={styles.dialogOverlay} onClick={() => setCurriculumToDelete(null)} />
          <div className={styles.dialog}>
            <div className={styles.dialogContent}>
              Are you sure you want to delete the curriculum "<em>{curriculumToDelete.name}</em>"?
              <br /><br />
              This action CANNOT BE UNDONE.
            </div>
            <div className={styles.dialogButtons}>
              <button onClick={() => handleDelete(curriculumToDelete.curriculum_id)} className={styles.dialogButton}>
                Yes, Delete
              </button>
              <button onClick={() => setCurriculumToDelete(null)} className={styles.dialogCancelButton}>
                Cancel
              </button>
            </div>
          </div>
        </>
      )}

      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.tableHeader}>Curriculum</th>
            <th className={styles.tableHeader}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredCurriculums.length > 0 ? (
            filteredCurriculums.map((c) => (
              <tr key={c.curriculum_id} className={styles.tableRow}>
                <td className={styles.tableCell}>
                  <a
                    href="#"
                    className={styles.tableLink}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/curriculums/${c.curriculum_id}`);
                    }}
                  >
                    {c.name}
                  </a>
                </td>
                <td className={styles.tableCell}>
                  <button
                    onClick={() => handleDeleteClick(c)}
                    className={styles.deleteButton}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={2} className={styles.tableCell}>No curriculums found.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CurriculumsPage;
